// src/Components/videochat/useCallChat.js
//
// El chat de la conversación dentro de la llamada. Son los MISMOS mensajes y el
// mismo historial que el chat normal (mismos endpoints y mismos eventos del
// relay): lo que se escribe acá aparece en el chat y al revés.
//
//  - Envío optimista: aparece al instante con id temporal, se envía por HTTP,
//    se reemplaza por el mensaje real y se emite `new-message` (el relay no se
//    lo devuelve a quien lo emitió).
//  - Si falla queda marcado; un toque lo reintenta, y al volver la conexión se
//    reintentan solos, en orden.
//  - «Escribiendo»: `typing` como mucho cada 2 s, `stop-typing` a los 3 s sin
//    teclear; el del otro se apaga solo a los 5 s.
import { useCallback, useEffect, useRef, useState } from 'react'
import axios from 'axios'
import config from '../../Config'
import { containsSensitiveInfo, MAX_MESSAGE_LENGTH } from './callChatUtils'

const API = `${config.REACT_APP_BASE_URL}/api/v1/chat`
const KEEP_MESSAGES = 60
const TYPING_EMIT_INTERVAL_MS = 2000
const TYPING_STOP_DEBOUNCE_MS = 3000
const PARTNER_TYPING_TIMEOUT_MS = 5000

const byDate = (a, b) => new Date(a.created_at) - new Date(b.created_at)

export default function useCallChat({ chatId, myId, authToken, socket, visible }) {
  const [messages, setMessages] = useState([])
  const [partnerTyping, setPartnerTyping] = useState(false)
  const [unseen, setUnseen] = useState(0)

  const headers = { Authorization: `Bearer ${authToken}` }
  const visibleRef = useRef(visible)
  visibleRef.current = visible
  const messagesRef = useRef(messages)
  messagesRef.current = messages
  const typingLastEmitRef = useRef(0)
  const typingStopRef = useRef(null)
  const partnerTypingRef = useRef(null)
  const sendingRef = useRef(false)

  const isMine = useCallback((m) => String(m.sender_id) === String(myId), [myId])

  const markRead = useCallback(() => {
    axios.post(`${API}/messages/${chatId}/mark-read`, null, { headers }).catch(() => {})
    try { socket?.emit('message-read', { chatId }) } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatId, socket, authToken])

  const addMessage = useCallback((message) => {
    setMessages((prev) => {
      if (prev.some((m) => m.id === message.id)) return prev
      return [...prev, message].sort(byDate).slice(-KEEP_MESSAGES)
    })
  }, [])

  // Historial: los mismos mensajes que el chat normal.
  const refetch = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/messages/${chatId}`, { headers, params: { page: 1 } })
      const fresh = (res.data?.data || []).slice().sort(byDate)
      setMessages((prev) => {
        const byId = new Map(prev.map((m) => [m.id, m]))
        fresh.forEach((m) => byId.set(m.id, { ...byId.get(m.id), ...m }))
        // Los que aún no salieron (id temporal) se conservan al final.
        return [...byId.values()].sort(byDate).slice(-KEEP_MESSAGES)
      })
      return fresh
    } catch {
      return []
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatId, authToken])

  useEffect(() => { refetch() }, [refetch])

  // Mensajes y «escribiendo» del otro, por el relay.
  useEffect(() => {
    if (!socket) return
    const onNew = ({ message, chatId: cid }) => {
      if (!message || String(cid) !== String(chatId)) return
      addMessage(message)
      clearTimeout(partnerTypingRef.current)
      setPartnerTyping(false)
      if (!isMine(message)) {
        if (visibleRef.current) markRead()
        else setUnseen((n) => n + 1)
      }
    }
    const onTyping = ({ chatId: cid }) => {
      if (String(cid) !== String(chatId)) return
      setPartnerTyping(true)
      clearTimeout(partnerTypingRef.current)
      partnerTypingRef.current = setTimeout(() => setPartnerTyping(false), PARTNER_TYPING_TIMEOUT_MS)
    }
    const onStopTyping = ({ chatId: cid }) => {
      if (String(cid) !== String(chatId)) return
      clearTimeout(partnerTypingRef.current)
      setPartnerTyping(false)
    }
    socket.on('new-message', onNew)
    socket.on('typing', onTyping)
    socket.on('stop-typing', onStopTyping)
    return () => {
      socket.off('new-message', onNew)
      socket.off('typing', onTyping)
      socket.off('stop-typing', onStopTyping)
      clearTimeout(partnerTypingRef.current)
    }
  }, [socket, chatId, addMessage, isMine, markRead])

  // Al mostrarse el chat, el contador vuelve a 0 y se marca leído.
  useEffect(() => {
    if (visible) {
      setUnseen(0)
      markRead()
    }
  }, [visible, markRead])

  const emitStopTyping = useCallback(() => {
    clearTimeout(typingStopRef.current)
    try { socket?.emit('stop-typing', { chatId }) } catch {}
  }, [socket, chatId])

  // Se llama con cada cambio del campo de texto.
  const handleInput = useCallback((value) => {
    if (!socket) return
    if (value.trim()) {
      const now = Date.now()
      if (now - typingLastEmitRef.current > TYPING_EMIT_INTERVAL_MS) {
        typingLastEmitRef.current = now
        socket.emit('typing', { chatId })
      }
      clearTimeout(typingStopRef.current)
      typingStopRef.current = setTimeout(() => socket.emit('stop-typing', { chatId }), TYPING_STOP_DEBOUNCE_MS)
    } else {
      emitStopTyping()
    }
  }, [socket, chatId, emitStopTyping])

  const deliver = useCallback(async (tempId, text) => {
    try {
      const res = await axios.post(`${API}/send/${chatId}`, { message: text, message_type: 'text' }, { headers })
      const real = res.data
      setMessages((prev) => {
        const without = prev.filter((m) => m.id !== tempId)
        return without.some((m) => m.id === real.id) ? without : [...without, real].sort(byDate)
      })
      try { socket?.emit('new-message', { chatId, message: real }) } catch {}
      return true
    } catch (err) {
      setMessages((prev) => prev.map((m) => (m.id === tempId ? { ...m, failed: true, sending: false } : m)))
      return false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatId, socket, authToken])

  // Devuelve { ok } o { blocked: true } si trae un correo o un teléfono.
  const send = useCallback(async (raw) => {
    const text = raw.slice(0, MAX_MESSAGE_LENGTH).trim()
    if (!text) return { ok: false }
    if (containsSensitiveInfo(text)) return { ok: false, blocked: true }
    if (sendingRef.current) return { ok: false }
    sendingRef.current = true
    emitStopTyping()
    const tempId = `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`
    addMessage({
      id: tempId, message: text, message_type: 'text', sender_id: myId,
      created_at: new Date().toISOString(), sending: true,
    })
    try { await deliver(tempId, text) } finally { sendingRef.current = false }
    return { ok: true }
  }, [addMessage, deliver, emitStopTyping, myId])

  const retry = useCallback(async (tempId) => {
    const m = messagesRef.current.find((x) => x.id === tempId)
    if (!m) return
    setMessages((prev) => prev.map((x) => (x.id === tempId ? { ...x, failed: false, sending: true } : x)))
    await deliver(tempId, m.message)
  }, [deliver])

  // Al volver la conexión se reintentan los que fallaron, en orden.
  useEffect(() => {
    const onOnline = async () => {
      const failed = messagesRef.current.filter((m) => m.failed)
      for (const m of failed) await retry(m.id)
    }
    window.addEventListener('online', onOnline)
    return () => window.removeEventListener('online', onOnline)
  }, [retry])

  useEffect(() => () => { clearTimeout(typingStopRef.current) }, [])

  return { messages, partnerTyping, unseen, send, retry, handleInput, refetch, addMessage, emitStopTyping, isMine }
}
